'use client';

import { createContext, useContext } from 'react';

import { useAnchorEl } from '../../..';

import type { MenuProps } from '@mui/material';
import type { Dispatch, FC, MouseEvent, ReactNode, SetStateAction } from 'react';

export interface AnchorElementProviderProps {
  children: ReactNode;
}

type UseAnchorElReturn = ReturnType<typeof useAnchorEl>;

interface MenuState {
  anchorEl: MenuProps['anchorEl'] | undefined;

  setAnchorEl: Dispatch<SetStateAction<MenuProps['anchorEl']>>;

  onClose: UseAnchorElReturn['handleClose'];

  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
}

const initialState: MenuState = {
  anchorEl: void 0,
  setAnchorEl: (element) => element,
  onClose: () => void 0,
  onClick: () => void 0,
};

export const Context = createContext( initialState );

export const useAnchorElContext = (): MenuState => useContext<MenuState>( Context );

/**
 * provides management of the anchor element and open/close state of the PopoverDisplay
 * requires a <AnchorElement> to consume event handler
 *
 * @example
 * ```jsx
 *   <AnchorElement.PopperProvider>
 *     <AnchorElement>
 *       <MenuItem>Profile</MenuItem>
 *     </AnchorElement>
 *     <AnchorElement>
 *       <MenuItem>My account</MenuItem>
 *     </AnchorElement>
 *     <AnchorElement>
 *       <MenuItem>Logout</MenuItem>
 *     </AnchorElement>
 *   </AnchorElement.PopperProvider>
 * ```
 */

export const AnchorElementProvider: FC<AnchorElementProviderProps> = ({
  children,
}) => {
  const { anchorEl, setAnchorEl, handleClose, handleClick } = useAnchorEl();

  const state = {
    anchorEl,
    setAnchorEl,
    onClose: handleClose,
    onClick: handleClick,
  };

  return (
    <Context.Provider value={state}>
      {children}
    </Context.Provider>
  );
};
